import { CdkDrag, type CdkDragEnd } from '@angular/cdk/drag-drop';
import { formatDate, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  LOCALE_ID,
  signal,
} from '@angular/core';
import {
  ActiveTelehealthCallService,
  DateParserService,
  MeService,
  ProfileTimeZoneService,
  TelehealthCallEngineService,
} from '@services';
import { TelehealthCallWindowComponent } from '../telehealth-call-window/telehealth-call-window.component';
import { TelehealthChatComponent } from '../telehealth-chat/telehealth-chat.component';

@Component({
  selector: 'icaredx-telehealth-call-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
  imports: [
    TelehealthCallWindowComponent,
    TelehealthChatComponent,
    CdkDrag,
    NgTemplateOutlet,
  ],
  templateUrl: './telehealth-call-panel.component.html',
})
export class TelehealthCallPanelComponent {
  protected readonly call = inject(ActiveTelehealthCallService);
  protected readonly engine = inject(TelehealthCallEngineService);
  private readonly me = inject(MeService);
  private readonly locale = inject(LOCALE_ID);
  private readonly dateParser = inject(DateParserService);
  private readonly profileTimeZone = inject(ProfileTimeZoneService);

  readonly placement = input<'floating' | 'page'>('floating');

  protected readonly dragPosition = signal<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  protected readonly visibleHere = computed(() => {
    const mode = this.call.displayMode();
    return this.placement() === 'page'
      ? mode === 'full'
      : mode === 'side' || mode === 'mini';
  });

  protected readonly counterpartyName = computed(() => {
    const appointment = this.call.activeAppointment();
    if (!appointment) {
      return '';
    }
    const isProvider = this.me.userId === appointment.providerId;
    return isProvider
      ? appointment.patientDisplayName
      : `Dr. ${appointment.providerDisplayName}`;
  });

  protected readonly callTitle = computed(() => {
    const counterparty = this.counterpartyName();
    return counterparty ? `Live with ${counterparty}` : '';
  });

  protected readonly localName = computed(() => {
    const me = this.me.me();
    return me ? `${me.givenName} ${me.familyName}`.trim() : '';
  });

  protected readonly displayTimeZone = computed(() =>
    this.profileTimeZone.timeZone()
  );

  protected readonly scheduleLabel = computed(() => {
    const appointment = this.call.activeAppointment();
    if (!appointment) {
      return '';
    }
    const timeZone = this.displayTimeZone();
    const format = (utc: string) => {
      const zoned = this.dateParser.toZonedWallClock(utc, timeZone);
      return zoned ? formatDate(zoned, 'hh:mma', this.locale) : '';
    };
    return `${format(appointment.startUtc)} - ${format(appointment.endUtc)}`;
  });

  protected readonly primaryActionLabel = computed(() => {
    const appointment = this.call.activeAppointment();
    const isProvider =
      !!appointment && this.me.userId === appointment.providerId;
    return isProvider ? 'Start Call' : 'Join';
  });

  constructor() {
    effect(() => {
      if (this.call.displayMode() !== 'mini') {
        this.dragPosition.set({ x: 0, y: 0 });
      }
    });
  }

  protected readonly isSideInCall = computed(
    () => this.call.displayMode() === 'side' && this.call.phase() === 'in-call'
  );

  protected readonly showChat = computed(() => {
    const mode = this.call.displayMode();
    return mode === 'full' || this.isSideInCall();
  });

  protected readonly containerClasses = computed(() => {
    const mode = this.call.displayMode();
    if (mode === 'full') {
      return 'z-0 flex w-full h-[calc(100vh-8rem)] gap-4 p-4';
    }
    const base =
      'flex flex-col overflow-hidden rounded-2xl text-white shadow-2xl';
    return mode === 'side'
      ? `${base} z-40 sticky top-4 self-start ml-4 mr-4 h-[calc(100%-2rem)] w-[32rem] gap-2 bg-neutral-950 p-2`
      : `${base} z-[60] fixed bottom-20 right-4 w-[32rem] bg-gray-800`;
  });

  protected readonly windowClasses = computed(() => {
    const mode = this.call.displayMode();
    if (mode === 'full') {
      return 'block flex-1 min-w-0 min-h-0 overflow-hidden rounded-2xl bg-gray-800 text-white';
    }
    if (mode !== 'side') {
      return this.call.phase() === 'in-call'
        ? 'block aspect-video w-full overflow-hidden bg-gray-800'
        : 'block h-[32rem] w-full overflow-hidden bg-gray-800';
    }
    // Side mode: the video is a rounded card inside the padded frame —
    // fixed aspect-ratio once in-call, growing to fill during preview.
    return this.isSideInCall()
      ? 'block aspect-video w-full shrink-0 overflow-hidden rounded-xl bg-gray-800'
      : 'block min-h-0 grow overflow-hidden rounded-xl bg-gray-800';
  });

  protected readonly chatClasses = computed(() =>
    this.call.displayMode() === 'full'
      ? 'flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden rounded-2xl bg-white text-gray-900 shadow-sm'
      : 'flex min-h-0 grow flex-col'
  );

  protected readonly chatTheme = computed<'dark' | 'light'>(() =>
    this.call.displayMode() === 'full' ? 'light' : 'dark'
  );

  protected onToggleMute(): void {
    void this.engine.toggleMute();
  }

  protected onToggleCamera(): void {
    void this.engine.toggleCamera();
  }

  protected onToggleScreenShare(): void {
    void this.engine.toggleScreenShare();
  }

  protected onSelectCamera(deviceId: string): void {
    void this.engine.selectCamera(deviceId);
  }

  protected onSelectMicrophone(deviceId: string): void {
    void this.engine.selectMicrophone(deviceId);
  }

  protected onSelectSpeaker(deviceId: string): void {
    void this.engine.selectSpeaker(deviceId);
  }

  protected onJoin(): void {
    void this.call.confirmJoin();
  }

  protected onRejoin(): void {
    void this.call.rejoin();
  }

  protected onHangUp(): void {
    void this.call.end();
  }

  protected onToggleSize(): void {
    this.call.setDisplayMode(
      this.call.displayMode() === 'side' ? 'mini' : 'side'
    );
  }

  protected onDragEnded(event: CdkDragEnd): void {
    this.dragPosition.set(event.source.getFreeDragPosition());
  }
}
